/* eslint-disable react/no-array-index-key */
import Heading from 'molecules/Heading';
import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';
import Video from 'molecules/Video';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import LottiePlayer from 'components/LottiePlayer/LottiePlayer';
import { SwitchbackConfigProps } from 'components/Switchback/config';
import {
  switchbackContentStyle,
  switchbackImageSectionStyle,
  switchbackImageWrapperStyle,
} from 'components/Switchback/styles';

import { buildBackgroundUrl } from 'utils/cloudinary/buildBackgroundUrl';
import { useIsDesktop } from 'utils/hooks/useIsDesktop';

import type { FC } from 'react';
import type React from 'react';

const getOptimizedDimensions = (origW: number, origH: number, maxWidth = 900) => {
  const ratio = origH / origW;

  if (origW > maxWidth) {
    return {
      width: maxWidth,
      height: Math.round(maxWidth * ratio),
    };
  }

  return {
    width: Math.min(origW, maxWidth),
    height: Math.round(Math.min(origW, maxWidth) * ratio),
  };
};

const getDynamicSizes = (width: number) => {
  // Mobile (<640px) Switchback layout stacks flex-col with the image taking
  // ~90vw of the viewport (container has px-4, no other side gutters at this
  // breakpoint). Using `90vw` instead of the prior `400px` literal matches
  // the actual rendered size (~348px on a 412px viewport) so the browser
  // picks a smaller srcset entry and Lighthouse stops flagging
  // "image larger than displayed dimensions".
  if (width <= 400) {
    return '(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 400px';
  } else if (width <= 600) {
    return '(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 600px';
  } else {
    // Image is constrained by max-w-screen-xl (1280px) + lg:basis-1/2, so the
    // displayed width never exceeds ~608px even on ultra-wide viewports. The
    // previous 800px fallback caused over-delivery (and a smaller srcset miss)
    // on >1536px screens with no visual benefit.
    return '(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 608px';
  }
};

const Switchback: FC<SwitchbackConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const {
    image,
    reverse,
    aboveTheFold = false,
    videoSrc,
    thumbnail,
    disableGradient = false,
    gradientHeight = 80,
    gradientWidth = 105,
    gradientTop = 40,
    mobileGradientHeight = 70,
    mobileGradientTop = 20,
    mobileGradientBottom = 60,
    useLottie,
    lottieFile,
    lottieUrl,
    useFeatureCard,
    useComparisonBoxes,
    comparisonBoxes,
    watermarkImage,
    backgroundImage,
    cardTitle,
    cardFeatures,
  } = rest;

  const { url, width: originalWidth, height: originalHeight } = image?.file || {};

  const { width: optimizedWidth, height: optimizedHeight } =
    originalWidth && originalHeight
      ? getOptimizedDimensions(originalWidth, originalHeight)
      : { width: 800, height: 600 };

  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;

  const finalGradientTop = isMobile ? mobileGradientTop : gradientTop;
  const finalGradientHeight = isMobile ? mobileGradientHeight : gradientHeight;

  let adjustedPaddingTop = sectionProps.paddingTop;
  let adjustedPaddingBottom = sectionProps.paddingBottom;

  if (sectionProps.paddingTop === 'lg') {
    adjustedPaddingTop = '2xl';
  }
  if (sectionProps.paddingBottom === 'lg') {
    adjustedPaddingBottom = 'xl';
  }

  const adjustedSectionProps = {
    ...sectionProps,
    paddingTop: adjustedPaddingTop,
    paddingBottom: adjustedPaddingBottom,
  };

  const lottieSrc = lottieUrl || lottieFile;
  const hasMedia = !!image?.file?.url || !!videoSrc || (useLottie && !!lottieSrc);

  const hasCard = !!useFeatureCard && !!cardFeatures?.length;

  const featureCard = hasCard ? (
    <div className="flex w-full justify-center lg:justify-end">
      <div
        className="
          flex w-[600px] max-w-full flex-col gap-8
          rounded-lg
          bg-dual-feature-left px-12
          py-14 pb-10 backdrop-blur-[5px]
        "
      >
        {cardTitle && <div className="text-2xl font-medium leading-8 text-white">{cardTitle}</div>}

        <div className="flex w-full flex-col gap-5">
          {cardFeatures?.map((feature, index) => (
            <div key={index} className="inline-flex w-full items-start gap-5">
              <div className="relative size-7 shrink-0 overflow-hidden rounded-2xl">
                <div className="absolute inset-0 size-6 overflow-hidden">
                  {feature.hasBullet && (
                    <Icon icon="circle-check" size={24} className="size-full rounded-full stroke-2 text-primary-1000" />
                  )}
                </div>
              </div>

              <div className="inline-flex flex-1 flex-col">
                <div className="text-lg font-bold leading-7 text-white">{feature.title}</div>
                <div className="text-sm leading-[24px] text-gray-300">{feature.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  ) : null;

  const comparisonContent = (
    <div
      className={`flex flex-col gap-10 lg:items-start lg:gap-[7%] ${reverse ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
    >
      <div className="w-full lg:-mt-2.5 lg:basis-[43%]">
        <Heading {...rest} alignment="left" sidePadding="none" />
      </div>

      <div className="flex w-full flex-col gap-8 lg:basis-1/2">
        {comparisonBoxes?.map((box, index) => (
          <ScrollFadeSection key={`${box.title}-${index}`} delay={0.1 + index * 0.35} containPaint={false}>
            <div className="relative">
              {box.isHighlighted && (
                <div aria-hidden className="absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-1/2">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-primary-300">
                    <circle cx="12" cy="12" r="9" fill="#101017" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M12 8.5v7M8.5 12h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
              )}

              <div
                className={`relative overflow-hidden rounded-lg border px-7 py-6 sm:px-10 ${
                  box.isHighlighted ? 'border-primary-300' : 'border-[#73737a]'
                }`}
              >
                {box.isHighlighted && (
                  <Image
                    url={watermarkImage?.file?.url || '/images/prismatic-icon-color.svg'}
                    alt={watermarkImage?.alt || ''}
                    noFill
                    width={171}
                    height={148}
                    className="pointer-events-none absolute -right-16 top-0 opacity-50"
                  />
                )}

                <div className="relative text-lg font-bold leading-[1.8] text-white">{box.title}</div>

                <ul
                  className={`relative mt-1 sm:columns-2 sm:gap-x-9 ${
                    box.isHighlighted ? 'list-none' : 'list-disc pl-5'
                  }`}
                >
                  {box.features?.map(feature => (
                    <li key={feature.text} className="break-inside-avoid text-[15px] leading-[1.8] text-gray-300">
                      {box.isHighlighted && (
                        <span aria-hidden className="mr-1.5">
                          +
                        </span>
                      )}
                      {feature.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </ScrollFadeSection>
        ))}
      </div>
    </div>
  );

  let innerContent = useFeatureCard ? (
    <div className="flex flex-col gap-10 lg:flex-row lg:items-start">
      <div className="w-full lg:basis-[52%]">
        <Heading {...rest} alignment="left" sidePadding="none" />
      </div>

      <div className="flex w-full items-stretch lg:basis-[48%]">{featureCard}</div>
    </div>
  ) : (
    <div className={switchbackContentStyle({ reverse: !!reverse })}>
      <div className="w-full lg:basis-1/2">
        <Heading {...rest} alignment="left" sidePadding="none" />
      </div>

      <div
        className={switchbackImageSectionStyle({ disableGradient })}
        style={
          {
            '--gradient-height': `${finalGradientHeight}%`,
            '--gradient-width': `${gradientWidth}%`,
            '--gradient-top': `${finalGradientTop}px`,
            '--gradient-bottom': `${mobileGradientBottom}px`,
          } as React.CSSProperties
        }
      >
        <div className={switchbackImageWrapperStyle({ isImage: hasMedia })}>
          {useLottie && lottieSrc && <LottiePlayer src={lottieSrc} className="z-30 size-full" />}

          {!useLottie && image?.file?.url && !videoSrc && (
            <Image
              url={url || ''}
              alt={image?.alt || ''}
              noFill
              width={optimizedWidth}
              height={optimizedHeight}
              objectCover
              className="z-30 size-full"
              sizes={getDynamicSizes(optimizedWidth)}
              priority={aboveTheFold}
            />
          )}

          {!useLottie && videoSrc && <Video videoSrc={videoSrc} thumbnail={thumbnail} bare />}
        </div>
      </div>
    </div>
  );

  if (useComparisonBoxes) {
    innerContent = comparisonContent;
  }

  const backgroundUrl = buildBackgroundUrl(backgroundImage?.file?.url);
  const showBackground = Boolean(backgroundUrl);

  const mobileTabletBgClasses = [
    'top-[415px] left-[35%] -translate-x-[50%]',
    'h-[650px] w-[720px] opacity-45 bg-center bg-[length:100%_auto]',
    'sm:top-[450px] sm:left-[40%] sm:h-[700px] sm:w-[780px]',
  ].join(' ');

  const desktopBgClasses = [
    '-top-[100px] right-[-250px] left-auto translate-x-0',
    'h-[850px] w-[min(90vw,1100px)] bg-right bg-[length:100%_auto] opacity-100',
  ].join(' ');

  const backgroundClass = showBackground
    ? ['pointer-events-none absolute -z-10 bg-no-repeat', isDesktop ? desktopBgClasses : mobileTabletBgClasses].join(
        ' ',
      )
    : '';

  return (
    <Section {...adjustedSectionProps} bare>
      <div className="relative w-full overflow-visible">
        {showBackground && (
          <div aria-hidden className={backgroundClass} style={{ backgroundImage: `url(${backgroundUrl})` }} />
        )}

        <div className="relative z-10 mx-auto max-w-screen-xl px-4 sm:px-16 lg:px-8">{innerContent}</div>
      </div>
    </Section>
  );
};

export default Switchback;
